import { useAuth0 } from "@auth0/auth0-vue";
import { ref, computed, watch, onMounted } from "vue";
import { createConsumer } from "@rails/actioncable";
import axios from "axios";
import { showLoadingIndicator } from "@/utils/loadingState.js";

export const useBase = () => {
  const apiUrl = import.meta.env.VITE_API_SERVER_URL;
  const socketUrl = import.meta.env.VITE_SOCKET_SERVER_URL;
  const socketConsumer = ref();
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  onMounted(async () => {
    try {
      await getAccessTokenSilently();
    } catch (e) {
      console.warn("Silent authentication failed:", e);
      await loginWithRedirect();
    }
  });

  const baseSave = async (path, resource, callback, errorCallback) => {
    showLoadingIndicator.value = true;
    const response = ref(null);
    const errors = ref();
    try {
      if (resource.id) {
        response.value = await axios.put(
          `${apiUrl}/api/${path}/${resource.id}.json`,
          { data: resource },
          { headers: await requestHeaders() },
        );
      } else {
        response.value = await axios.post(
          `${apiUrl}/api/${path}.json`,
          { data: resource },
          { headers: await requestHeaders() },
        );
      }
      callback && callback(response.value.data.data);
    } catch (err) {
      if (errorCallback) {
        errors.value = parseErrors(err.response.data.errors);
        errorCallback(errors);
      } else {
        throw err;
      }
    } finally {
      showLoadingIndicator.value = false;
    }
  };

  const baseMultipartSave = async (path, resource, callback, errorCallback) => {
    showLoadingIndicator.value = true;
    const response = ref(null);
    const errors = ref();
    const headers = await requestHeaders();
    headers["Content-Type"] = "multipart/form-data";
    try {
      if (resource.id) {
        response.value = await axios.put(
          `${apiUrl}/api/${path}/${resource.id}.json`,
          { data: resource },
          { headers: headers },
        );
      } else {
        response.value = await axios.post(
          `${apiUrl}/api/${path}.json`,
          { data: resource },
          { headers: headers },
        );
      }
      callback && callback(response.value.data.data);
    } catch (err) {
      if (errorCallback) {
        errors.value = parseErrors(err.response.data.errors);
        errorCallback(errors);
      } else {
        throw err;
      }
    } finally {
      showLoadingIndicator.value = false;
    }
  };

  const basePost = async (path, data, callback, errorCallback, extraParams = {}) => {
    showLoadingIndicator.value = true;
    const response = ref(null);
    const errors = ref();
    try {
      const query = buildQueryParams(extraParams);
      response.value = await axios.post(
        `${apiUrl}/api/${path}.json${query}`,
        { data: data },
        { headers: await requestHeaders() },
      );
      callback && callback(response.value.data.data);
    } catch (err) {
      if (errorCallback) {
        errors.value = parseErrors(err.response.data.errors);
        errorCallback(errors);
      } else {
        throw err;
      }
    } finally {
      showLoadingIndicator.value = false;
    }
  };

  const baseMultipartPost = async (path, data, callback, errorCallback) => {
    showLoadingIndicator.value = true;
    const response = ref(null);
    const errors = ref();
    const headers = await requestHeaders();
    headers["Content-Type"] = "multipart/form-data";
    try {
      response.value = await axios.post(`${apiUrl}/api/${path}.json`, data, { headers: headers });
      callback && callback(response.value.data.data);
    } catch (err) {
      if (errorCallback) {
        errors.value = parseErrors(err.response.data.errors);
        errorCallback(errors);
      } else {
        throw err;
      }
    } finally {
      showLoadingIndicator.value = false;
    }
  };

  const basePut = async (path, data, callback, errorCallback) => {
    showLoadingIndicator.value = true;
    const response = ref(null);
    const errors = ref();
    try {
      response.value = await axios.put(
        `${apiUrl}/api/${path}.json`,
        { data: data },
        { headers: await requestHeaders() },
      );
      callback && callback(response.value.data.data);
    } catch (err) {
      if (errorCallback) {
        errors.value = parseErrors(err.response.data.errors);
        errorCallback(errors);
      } else {
        throw err;
      }
    } finally {
      showLoadingIndicator.value = false;
    }
  };

  const buildQueryParams = (params = {}) => {
    const query = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .map(([k, v]) => {
        if (Array.isArray(v)) {
          return `${k}=${v.join(",")}`;
        }
        return `${k}=${v}`;
      })
      .join("&");
    return query ? `?${query}` : "";
  };

  const baseFetchAll = async (path, errorCallback, includes = [], options = {}) => {
    showLoadingIndicator.value = true;
    const errors = ref();
    try {
      const params = {};
      if (includes && includes.length > 0) {
        params.include = Array.isArray(includes) ? includes.join(",") : includes;
      }

      if (!options.paginate) {
        const query = buildQueryParams(params);
        const response = await axios.get(`${apiUrl}/api/${path}.json${query}`, {
          headers: await requestHeaders(),
        });
        return response.data.data;
      }

      const perPage = options.perPage || 50;
      let currentPage = 1;
      const allData = [];
      const headers = await requestHeaders();

      while (true) {
        const query = buildQueryParams({ ...params, page: currentPage, per_page: perPage });
        const response = await axios.get(`${apiUrl}/api/${path}.json${query}`, { headers });
        const pageData = response.data.data || [];
        allData.push(...pageData);

        const totalPages = response.data?.meta?.totalPages;
        if (totalPages && currentPage >= totalPages) {
          break;
        }

        if (pageData.length < perPage) {
          break;
        }

        currentPage += 1;
      }

      return allData;
    } catch (err) {
      if (errorCallback) {
        errors.value = parseErrors(err.response.data.errors);
        errorCallback(errors);
      } else {
        throw err;
      }
    } finally {
      showLoadingIndicator.value = false;
    }
  };

  const baseFetchPage = async (path, errorCallback, includes = [], options = {}) => {
    showLoadingIndicator.value = true;
    const errors = ref();
    try {
      const params = {};
      if (includes && includes.length > 0) {
        params.include = Array.isArray(includes) ? includes.join(",") : includes;
      }

      const page = options.page || 1;
      const perPage = options.perPage || 10;
      const query = buildQueryParams({
        ...params,
        ...(options.extraParams || {}),
        page: page,
        per_page: perPage,
      });
      const response = await axios.get(`${apiUrl}/api/${path}.json${query}`, {
        headers: await requestHeaders(),
      });

      return {
        data: response.data?.data || [],
        meta: response.data?.meta || { page, perPage, totalCount: 0, totalPages: 0 },
      };
    } catch (err) {
      if (errorCallback) {
        errors.value = parseErrors(err.response.data.errors);
        errorCallback(errors);
        return {
          data: [],
          meta: {
            page: options.page || 1,
            perPage: options.perPage || 10,
            totalCount: 0,
            totalPages: 0,
          },
        };
      }
      throw err;
    } finally {
      showLoadingIndicator.value = false;
    }
  };

  const baseFetchOne = async (path, id, errorCallback, includes = [], extraParams = {}) => {
    showLoadingIndicator.value = true;
    const errors = ref();
    try {
      const params = { ...extraParams };
      if (includes && includes.length > 0) {
        params.include = Array.isArray(includes) ? includes.join(",") : includes;
      }
      const query = buildQueryParams(params);
      const response = await axios.get(`${apiUrl}/api/${path}/${id}.json${query}`, {
        headers: await requestHeaders(),
      });
      return response.data.data;
    } catch (err) {
      if (errorCallback) {
        errors.value = parseErrors(err.response.data.errors);
        errorCallback(errors);
      } else {
        throw err;
      }
    } finally {
      showLoadingIndicator.value = false;
    }
  };

  const baseDelete = async (path, id, callback, errorCallback) => {
    showLoadingIndicator.value = true;
    const errors = ref();
    try {
      await axios.delete(`${apiUrl}/api/${path}/${id}.json`, { headers: await requestHeaders() });
      callback && callback();
    } catch (err) {
      if (errorCallback) {
        errors.value = parseErrors(err.response.data.errors);
        errorCallback(errors);
      } else {
        throw err;
      }
    } finally {
      showLoadingIndicator.value = false;
    }
  };

  const requestHeaders = async () => {
    try {
      const token = await getAccessTokenSilently();
      return {
        Authorization: `Bearer ${token}`,
      };
    } catch (e) {
      console.warn("Failed to get token for headers:", e);
      await loginWithRedirect();
      // Return empty headers to prevent TypeError if loginWithRedirect
      // doesn't immediately navigate away (e.g. popup mode)
      return {};
    }
  };

  const parseErrors = (errors) => {
    const errorMap = {};
    if (Array.isArray(errors)) {
      errors.forEach((err) => {
        const pointer = err.source?.pointer;
        const field = pointer ? pointer.split("/").slice(2).join(".") : null;
        if (field) {
          errorMap[field] = err.detail;
        } else if (err.detail) {
          // Global/non-field errors (e.g. SaleListingExtractionError with pointer "/data")
          const key = err.code || "base";
          errorMap[key] = err.detail;
        }
      });
    }
    return errorMap;
  };

  const watchRemoteResourceChange = async (resourceType, callback, global = false) => {
    if (!socketConsumer.value) {
      try {
        const token = await getAccessTokenSilently();
        socketConsumer.value = createConsumer(`${socketUrl}?token=${token}`);
      } catch (e) {
        console.warn("WebSocket auth failed:", e);
        await loginWithRedirect();
        return;
      }
    }

    socketConsumer.value.subscriptions.create(
      {
        channel: "ResourceChannel",
        room: resourceType,
        global: global,
      },
      {
        received(data) {
          callback(data);
        },
      },
    );
  };

  return {
    baseFetchOne,
    baseFetchAll,
    baseFetchPage,
    baseSave,
    baseMultipartSave,
    basePost,
    baseMultipartPost,
    basePut,
    baseDelete,
    watchRemoteResourceChange,
    parseErrors,
  };
};

export const useServerPaginatedCollection = ({
  resources,
  fetchPage,
  itemsPerPage = 10,
  searchQuery = null,
  searchDebounceMs = 300,
  filters = null,
}) => {
  const currentPage = ref(1);
  const totalPages = ref(0);
  const totalCount = ref(0);
  const lastIncludes = ref([]);
  const lastErrorCallback = ref(null);
  const searchDebounceTimer = ref(null);

  const buildExtraParams = () => {
    const extra = {};
    if (searchQuery?.value) extra.search = searchQuery.value;
    if (filters?.value) {
      Object.entries(filters.value).forEach(([key, val]) => {
        if (val) extra[key] = val;
      });
    }
    return extra;
  };

  const paginationRange = computed(() => {
    const total = totalPages.value;
    const current = currentPage.value;
    const delta = 2;

    if (total <= 5) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const range = [];
    range.push(1);

    if (current > delta + 2) {
      range.push("...");
    }

    const start = Math.max(2, current - delta);
    const end = Math.min(total - 1, current + delta);

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    if (current < total - delta - 1) {
      range.push("...");
    }

    range.push(total);
    return range;
  });

  const fetchAll = async (errorCallback, includes = [], page = 1) => {
    lastErrorCallback.value = errorCallback;
    lastIncludes.value = includes;

    const response = await fetchPage(errorCallback, includes, page, itemsPerPage, buildExtraParams());
    resources.value = response.data;
    currentPage.value = response.meta?.page || page;
    totalPages.value = response.meta?.totalPages || 0;
    totalCount.value = response.meta?.totalCount || 0;

    if (totalPages.value > 0 && currentPage.value > totalPages.value) {
      await fetchAll(lastErrorCallback.value, lastIncludes.value, totalPages.value);
    }
  };

  const isFetchingNextPage = ref(false);
  const hasNextPage = computed(() => currentPage.value < totalPages.value);

  const fetchNextPage = async () => {
    if (!hasNextPage.value || isFetchingNextPage.value) return;

    isFetchingNextPage.value = true;
    try {
      const nextPage = currentPage.value + 1;
      const response = await fetchPage(
        lastErrorCallback.value,
        lastIncludes.value,
        nextPage,
        itemsPerPage,
        buildExtraParams(),
      );
      resources.value = [...resources.value, ...response.data];
      currentPage.value = response.meta?.page || nextPage;
      totalPages.value = response.meta?.totalPages || totalPages.value;
      totalCount.value = response.meta?.totalCount || totalCount.value;
    } finally {
      isFetchingNextPage.value = false;
    }
  };

  const goToPage = async (page) => {
    if (page < 1 || page > totalPages.value || page === currentPage.value) {
      return;
    }
    await fetchAll(lastErrorCallback.value, lastIncludes.value, page);
  };

  const goToNextPage = async () => {
    await goToPage(currentPage.value + 1);
  };

  const goToPreviousPage = async () => {
    await goToPage(currentPage.value - 1);
  };

  if (searchQuery) {
    watch(searchQuery, () => {
      if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value);
      }

      searchDebounceTimer.value = setTimeout(() => {
        fetchAll(lastErrorCallback.value, lastIncludes.value, 1);
      }, searchDebounceMs);
    });
  }

  if (filters) {
    watch(filters, () => {
      fetchAll(lastErrorCallback.value, lastIncludes.value, 1);
    }, { deep: true });
  }

  return {
    fetchAll,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    currentPage,
    totalPages,
    totalCount,
    paginationRange,
  };
};
